import { CONFIG } from '@/adapters/primitive/_data/config/config';
import { episodeCommentsContext } from '@/_pure/episodeComments';
import { detachPrimitiveEpisode } from '@/adapters/primitive/_pure/episode';
import { primitiveDeveloperEpisodes, primitiveEpisodes, resolvePrimitiveEpisode } from '@/adapters/primitive/episodeCatalog';
import { primitiveEpisodeSupport } from './episodeSupport';
import { runtimeController } from '@/adapters/primitive/runtime';
import type { EpisodePresentation } from '@/_contract/types';
import type { Config, CosmosDataModel, RuntimeState } from '@/adapters/primitive/_contract/config';

export const episodePresentation: EpisodePresentation<Config, RuntimeState, CosmosDataModel> = {
    support: primitiveEpisodeSupport,
    episodes: primitiveEpisodes,
    developerEpisodes: primitiveDeveloperEpisodes,
    nextEpisode: (state, intent) => {
        if (intent.overlayIntentKind !== 'action-triggered' || intent.actionId !== 'episode-next') return undefined;
        const playback = state.episode;
        if (!playback) return undefined;
        const { episode, scene } = resolvePrimitiveEpisode(playback.episodeHandle, state.sceneIndex ?? 0);
        if ((state.sceneIndex ?? 0) !== episode.scenes.length - 1
            || playback.beatIndex !== scene.beats.length - 1) return undefined;
        const index = primitiveEpisodes.findIndex(entry => entry.handle === episode.handle);
        return index < 0 ? undefined : primitiveEpisodes[index + 1]?.handle;
    },
    enter: (args) => {
        const state = { ...args.runtimeState, pendingStepIndex: null, booleanAssignment: undefined,
            isPlaying: false, episode: undefined };
        const next = runtimeController.reduceOverlayIntent?.({ ...args, runtimeState: state,
            intent: { overlayIntentKind: 'select-changed', sectionId: 'primitive-episode', value: args.handle } });
        if (!next?.episode) throw new Error(`Could not enter Primitive Episode '${args.handle}'.`);
        return { ...next, episode: { ...next.episode,
            soundEnabled: args.runtimeState.episode?.soundEnabled ?? next.episode.soundEnabled } };
    },
    detach: state => detachPrimitiveEpisode(state, CONFIG),
    playback: (state) => state.episode,
    comments: state => {
        if (!state.episode) return undefined;
        const { episode } = resolvePrimitiveEpisode(state.episode.episodeHandle, state.sceneIndex ?? 0);
        return episodeCommentsContext(episode, state.sceneIndex ?? 0, state.episode.beatIndex);
    },
    inspectPlan: (state) => {
        if (!state.episode) throw new Error('No active Primitive ScenePlan.');
        const { scene } = primitiveEpisodeSupport.resolveScene(state.episode.episodeHandle, state.sceneIndex ?? 0);
        return scene.sceneKind === 'trace'
            ? { schemaVersion: 1, title: scene.title, trace: scene.trace, beats: scene.beats,
                ...(scene.excerpt ? { excerpt: scene.excerpt } : {}) }
            : scene;
    },
    pause: (state) => ({ ...state, isPlaying: false, episode: state.episode
        ? { ...state.episode, isPlaying: false, narrationRequested: false } : undefined }),
    settled: (state) => state.pendingStepIndex === null && state.booleanAssignment === undefined,
    overlay: (model) => ({ ...model, sections: model.sections.filter((section) => section.sectionId !== 'primitive-episode') }),
};
